import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Share2, UserPlus, X } from "lucide-react";
import { useEffect, useState } from "react";

import { ROLE_HINT, ROLE_LABEL, sharingApi, type ShareRole, type UserHit } from "../api/collab";
import { Avatar, Badge, Banner, Button, Field, Input, Modal, Select, SkeletonRows, useConfirm, useToast } from "../ds";

const ASSIGNABLE: ShareRole[] = ["viewer", "editor", "co_owner"];

/** "Share" dialog: who the report is shared with (Viewer / Editor / Co-owner).
 * Rules are enforced by the server; the UI only mirrors them. Used by the report
 * page and exported for the studio top bar:
 *   <ShareDialog open={open} onClose={() => setOpen(false)} reportId={id} reportName={name} /> */
export function ShareDialog({ open, onClose, reportId, reportName }: { open: boolean; onClose: () => void; reportId: string; reportName?: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const key = ["sharing", reportId];
  const sharing = useQuery({ queryKey: key, queryFn: () => sharingApi.get(reportId), enabled: open });
  const [text, setText] = useState("");
  const [debounced, setDebounced] = useState("");
  const [role, setRole] = useState<ShareRole>("viewer");

  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(text.trim()), 250);
    return () => window.clearTimeout(t);
  }, [text]);
  useEffect(() => {
    if (!open) {
      setText("");
      setRole("viewer");
    }
  }, [open]);

  const hits = useQuery({
    queryKey: ["people-search", debounced],
    queryFn: () => sharingApi.searchPeople(debounced),
    enabled: open && debounced.length >= 2,
    staleTime: 30_000,
  });
  const onDone = (data: unknown) => qc.setQueryData(key, data);
  const fail = (title: string) => (e: unknown) => toast.error(title, e instanceof Error ? e.message : undefined);

  const add = useMutation({
    mutationFn: (v: { user: UserHit; role: ShareRole }) => sharingApi.setPerson(reportId, v.user.id, v.role),
    onSuccess: (data, v) => {
      onDone(data);
      setText("");
      toast.success("Shared", `${v.user.name} is now a ${ROLE_LABEL[v.role].toLowerCase()}.`);
      if (!v.user.can_use_platform) toast.info("No access to the platform yet", `${v.user.name} needs a role from IT before they can sign in to reports.`);
    },
    onError: fail("Couldn't share"),
  });
  const change = useMutation({
    mutationFn: (v: { id: string; role: ShareRole }) => sharingApi.setPerson(reportId, v.id, v.role),
    onSuccess: onDone,
    onError: fail("Couldn't change the role"),
  });
  const remove = useMutation({
    mutationFn: (id: string) => sharingApi.removePerson(reportId, id),
    onSuccess: (data) => {
      onDone(data);
      toast.success("Access removed");
    },
    onError: fail("Couldn't remove access"),
  });

  const data = sharing.data;
  const existing = new Set((data?.people ?? []).map((p) => p.user_id));
  const results = (hits.data ?? []).filter((h) => !existing.has(h.id));

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={<span className="ds-row" style={{ gap: 8 }}><Share2 size={18} /> Share{reportName ? ` “${reportName}”` : ""}</span>}
      description="People you add can open the published report. Putting it on TV screens or sending a public link needs IT approval."
      footer={<Button variant="primary" onClick={onClose}>Done</Button>}
    >
      {sharing.isLoading ? (
        <SkeletonRows rows={3} />
      ) : sharing.isError ? (
        <Banner tone="danger" title="Couldn't load who has access">{sharing.error instanceof Error ? sharing.error.message : null}</Banner>
      ) : data ? (
        <div className="ds-stack">
          {data.can_manage ? (
            <div>
              <div className="ds-row" style={{ gap: 8, alignItems: "flex-end" }}>
                <div style={{ flex: 1 }}>
                  <Field label="Add people" hint="Search by name or email.">
                    <Input value={text} placeholder="Start typing a name…" onChange={(e) => setText(e.target.value)} autoFocus />
                  </Field>
                </div>
                <Field label="As">
                  <Select value={role} onChange={(e) => setRole(e.target.value as ShareRole)} title={ROLE_HINT[role]}>
                    {ASSIGNABLE.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
                  </Select>
                </Field>
              </div>
              {debounced.length >= 2 ? (
                <div role="listbox" aria-label="People" style={{ marginTop: 6, border: "1px solid var(--divider)", borderRadius: 8, maxHeight: 190, overflow: "auto" }}>
                  {hits.isLoading ? <div className="ds-small ds-muted" style={{ padding: 10 }}>Searching…</div> : null}
                  {!hits.isLoading && results.length === 0 ? <div className="ds-small ds-muted" style={{ padding: 10 }}>No one found (people already added are hidden).</div> : null}
                  {results.map((u) => (
                    <button
                      key={u.id}
                      type="button"
                      role="option"
                      className="ds-btn ds-btn--ghost"
                      style={{ width: "100%", justifyContent: "flex-start", gap: 10, borderRadius: 0, height: "auto", padding: "8px 10px" }}
                      disabled={add.isPending}
                      onClick={() => add.mutate({ user: u, role })}
                    >
                      <Avatar name={u.name} />
                      <span style={{ textAlign: "left" }}>
                        <div style={{ fontWeight: 600 }}>{u.name}</div>
                        <div className="ds-small ds-muted">{u.email}</div>
                      </span>
                      <span className="ds-grow" />
                      {!u.can_use_platform ? <Badge tone="warning">No platform role</Badge> : null}
                      <UserPlus size={15} />
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          ) : (
            <Banner tone="info" title="You can see who has access, but not change it">Ask the owner or a co-owner to add or remove people.</Banner>
          )}

          <div>
            <div className="ds-small ds-muted" style={{ marginBottom: 6 }}>People with access</div>
            <div className="ds-stack" style={{ gap: 2 }}>
              {data.people.map((p) => {
                const locked = !data.can_manage || p.is_creator || p.has_publish || (p.is_me && data.my_role !== "admin");
                return (
                  <div key={p.user_id} className="ds-row" style={{ gap: 10, padding: "6px 0", borderBottom: "1px solid var(--divider)" }}>
                    <Avatar name={p.name} />
                    <div className="ds-grow" style={{ minWidth: 0 }}>
                      <div className="ds-truncate" style={{ fontWeight: 600 }}>{p.name}{p.is_me ? " (you)" : ""}</div>
                      <div className="ds-small ds-muted ds-truncate">{p.email}</div>
                    </div>
                    {p.is_creator ? (
                      <Badge tone="info">Owner</Badge>
                    ) : locked ? (
                      <Badge tone="neutral" title={p.has_publish ? "Set by IT. Ask IT to change it." : undefined}>{ROLE_LABEL[p.role] ?? p.role}</Badge>
                    ) : (
                      <>
                        <Select
                          inputSize="sm"
                          style={{ width: 130, flex: "none" }}
                          value={p.role === "custom" ? "viewer" : p.role}
                          disabled={change.isPending}
                          onChange={(e) => change.mutate({ id: p.user_id, role: e.target.value as ShareRole })}
                          aria-label={`Role for ${p.name}`}
                        >
                          {ASSIGNABLE.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
                        </Select>
                        <Button
                          size="sm"
                          variant="ghost"
                          icon={X}
                          aria-label={`Remove ${p.name}`}
                          title="Remove access"
                          loading={remove.isPending && remove.variables === p.user_id}
                          onClick={async () => {
                            if (await confirm({ title: `Remove ${p.name}?`, message: "They can no longer open this report.", confirmLabel: "Remove", tone: "danger" })) remove.mutate(p.user_id);
                          }}
                        />
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          <div className="ds-small ds-faint">
            {ASSIGNABLE.map((r) => `${ROLE_LABEL[r]}: ${ROLE_HINT[r].toLowerCase()}`).join("  ·  ")}
          </div>
        </div>
      ) : null}
    </Modal>
  );
}
